/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import axios from 'axios';
import { APP_VERSION } from './version';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// REGLE DE GESTION V3.9.0 : une seule relance du refresh token a la fois.
let projethubRefreshPromise = null;

// REGLE DE GESTION : une expiration du jeton d'acces ne doit jamais faire perdre
// la page ou la saisie en cours. Le navigateur tente une seule fois de renouveler
// la session avec le refresh token HttpOnly puis rejoue la requete initiale.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = String(original.url || '');
    const refreshExcluded = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/google');
    if (status === 401 && !original._projethubRetry && !refreshExcluded) {
      original._projethubRetry = true;
      try {
        if (!projethubRefreshPromise) {
          projethubRefreshPromise = axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true }).finally(() => { projethubRefreshPromise = null; });
        }
        await projethubRefreshPromise;
        return api.request(original);
      } catch (_) {
        // AuthContext decide ensuite si la session doit etre fermee.
      }
    }
    if (!url.includes('/client-errors')) {
      const apiError = {
        api_method: String(original.method || 'GET').toUpperCase(),
        api_path: url,
        api_status: status || 0,
        message: error.response?.data?.detail || error.message || `Erreur API ${status || ''}`,
      };
      if (typeof window !== 'undefined') window.__PROJETHUB_LAST_API_ERROR__ = apiError;
      // Les erreurs serveur sont journalisees meme si React ne plante pas.
      if (status >= 500) reportClientError({ ...apiError, action: 'Appel API' });
    }
    return Promise.reject(error);
  }
);


// ==================== TELEMETRIE FRONTEND V4 ====================
// REGLE DE GESTION : ne jamais envoyer de formulaire, cookie ou token dans les logs.
export function reportClientError(payload = {}) {
  const safe = {
    page: payload.page || window.location.pathname,
    action: payload.action || undefined,
    error_name: payload.error_name || undefined,
    message: String(payload.message || 'Erreur navigateur').slice(0, 2000),
    stack: payload.stack ? String(payload.stack).slice(0, 12000) : undefined,
    component_stack: payload.component_stack ? String(payload.component_stack).slice(0, 8000) : undefined,
    browser: typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 700) : undefined,
    app_version: APP_VERSION,
    api_method: payload.api_method || undefined,
    api_path: payload.api_path ? String(payload.api_path).split('?')[0].slice(0, 500) : undefined,
    api_status: payload.api_status || undefined,
  };
  const base = API_URL || '';
  return axios.post(`${base}/api/client-errors`, safe, { withCredentials: true, timeout: 4000 }).catch(() => null);
}

export function formatApiError(error) {
  const detail = error.response?.data?.detail;
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg || JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}


// ==================== PROFILE ====================
export const profileApi = {
  update: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/profile/password', data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/profile/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAvatar: () => api.delete('/auth/profile/avatar'),
};


// ==================== CONFIGURATION DYNAMIQUE V3 ====================
export const configurationApi = {
  get: () => api.get('/configuration'),
  updateMenus: (menus) => api.put('/configuration/menus', { menus }),
  updateReferentialTypes: (types) => api.put('/configuration/referential-types', { types }),
};

// ==================== CLIENTS ====================
export const clientsApi = {
  getAll: () => api.get('/clients'),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// ==================== PROJECTS ====================
export const projectsApi = {
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  assignUsers: (projectId, userIds) => api.post(`/projects/${projectId}/assign`, userIds),
  uploadAvatar: (projectId, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/projects/${projectId}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  deleteAvatar: (projectId) => api.delete(`/projects/${projectId}/avatar`),
  bulkUpdate: (data) => api.post('/projects/bulk', data),
  bulkClosureCheck: (projectIds) => api.post('/projects/bulk/closure-check', { project_ids: projectIds }),
  closureCheck: (projectId) => api.get(`/projects/${projectId}/closure-check`),
};

export const projectTemplatesApi = {
  getAll: (includeInactive = false) => api.get('/project-templates', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/project-templates', data),
  update: (id, data) => api.put(`/project-templates/${id}`, data),
  duplicate: (id) => api.post(`/project-templates/${id}/duplicate`),
  delete: (id) => api.delete(`/project-templates/${id}`),
};

// ==================== TASKS ====================
export const tasksApi = {
  getWorkflow: () => api.get('/tasks/workflow/config'),
  updateWorkflow: (data) => api.put('/tasks/workflow/config', data),
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  getGantt: (projectId) => api.get(`/tasks/gantt/${projectId}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  dependencyImpact: (id) => api.get(`/tasks/${id}/dependency-impact`),
  selfAssign: (id) => api.post(`/tasks/${id}/self-assign`),
  importCSV: (projectId, file) => {
    const formData = new FormData();
    formData.append('project_id', projectId);
    formData.append('file', file);
    return api.post('/tasks/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportExcelUrl: (params) => `${API_URL}/api/tasks/export-excel?${new URLSearchParams(params).toString()}`,
};

// ==================== PLANIFICATION CHARGE V7 ====================
export const taskPlanningApi = {
  get: (taskId) => api.get(`/task-planning/${taskId}`),
  preview: (data) => api.post('/task-planning/preview', data),
  save: (taskId, data) => api.put(`/task-planning/${taskId}`, data),
  reset: (taskId) => api.delete(`/task-planning/${taskId}`),
};

// ==================== REQUIREMENTS ====================
export const requirementsApi = {
  getAll: (params) => api.get('/requirements', { params }),
  getById: (id) => api.get(`/requirements/${id}`),
  create: (data) => api.post('/requirements', data),
  update: (id, data) => api.put(`/requirements/${id}`, data),
  delete: (id) => api.delete(`/requirements/${id}`),
  uploadAttachment: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/requirements/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  deleteAttachment: (id, attachmentId) => api.delete(`/requirements/${id}/attachments/${attachmentId}`),
  getTemplates: () => api.get('/requirements/templates'),
  createTemplate: (data) => api.post('/requirements/templates', data),
  updateTemplate: (id, data) => api.put(`/requirements/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/requirements/templates/${id}`),
  exportExcelUrl: (projectId='') => `${API_URL}/api/requirements/export/xlsx${projectId ? `?project_id=${projectId}` : ''}`,
  exportPdfUrl: (projectId='') => `${API_URL}/api/requirements/export/pdf${projectId ? `?project_id=${projectId}` : ''}`,
};

// ==================== TIME TRACKING ====================
export const timeApi = {
  getAll: (params) => api.get('/time', { params }),
  getMy: () => api.get('/time/my'),
  getTeam: () => api.get('/time/team'),
  create: (data) => api.post('/time', data),
  update: (id, data) => api.put(`/time/${id}`, data),
  delete: (id) => api.delete(`/time/${id}`),
  validate: (id) => api.post(`/time/${id}/validate`),
  getCRA: (params) => api.get('/time/cra', { params }),
  exportCRAExcel: (params) => `${API_URL}/api/time/cra/export-excel?${new URLSearchParams(params).toString()}`,
};

// ==================== TEST SCENARIOS ====================
export const testsApi = {
  getAll: (params) => api.get('/tests', { params }),
  getById: (id) => api.get(`/tests/${id}`),
  create: (data) => api.post('/tests', data),
  update: (id, data) => api.put(`/tests/${id}`, data),
  delete: (id) => api.delete(`/tests/${id}`),
  executeStep: (testId, stepData) => api.post(`/tests/${testId}/execute-step`, stepData),
  uploadScreenshot: (testId, stepIndex, file) => {
    const formData = new FormData();
    formData.append('step_index', stepIndex);
    formData.append('file', file);
    return api.post(`/tests/${testId}/upload-screenshot`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importCSV: (projectId, file) => {
    const formData = new FormData();
    formData.append('project_id', projectId);
    formData.append('file', file);
    return api.post('/tests/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importExcel: (projectId, file) => {
    const formData = new FormData();
    formData.append('project_id', projectId);
    formData.append('file', file);
    return api.post('/tests/imports/excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  xlsxTemplateUrl: `${API_URL}/api/tests/templates/xlsx`,
  exportExcelUrl: (projectId) => `${API_URL}/api/tests/reports/export-excel${projectId ? `?project_id=${projectId}` : ''}`,
  exportPdfUrl: (projectId) => `${API_URL}/api/tests/reports/export-pdf${projectId ? `?project_id=${projectId}` : ''}`,
  qualityDashboard: (projectId='') => api.get('/tests/quality-dashboard', { params: projectId ? { project_id: projectId } : {} }),
};

// ==================== TEAMS ====================
export const teamsApi = {
  getAll: (serviceId) => api.get('/teams', { params: { service_id: serviceId } }),
  getById: (id) => api.get(`/teams/${id}`),
  getAvailableUsers: (id) => api.get(`/teams/${id}/available-users`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  addMember: (teamId, userId) => api.post(`/teams/${teamId}/members/${userId}`),
  removeMember: (teamId, userId) => api.delete(`/teams/${teamId}/members/${userId}`),
};

// ==================== SERVICES ====================
export const servicesApi = {
  getAll: () => api.get('/services'),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// ==================== MILESTONES ====================
export const milestonesApi = {
  getAll: (params) => api.get('/milestones', { params }),
  create: (data) => api.post('/milestones', data),
  update: (id, data) => api.put(`/milestones/${id}`, data),
  delete: (id) => api.delete(`/milestones/${id}`),
};

// ==================== SPRINTS ====================
export const sprintsApi = {
  getAll: (params) => api.get('/sprints', { params }),
  getById: (id) => api.get(`/sprints/${id}`),
  create: (data) => api.post('/sprints', data),
  update: (id, data) => api.put(`/sprints/${id}`, data),
  getTasks: (sprintId) => api.get(`/sprints/${sprintId}/tasks`),
  addTask: (sprintId, taskId) => api.post(`/sprints/${sprintId}/tasks/${taskId}`),
  removeTask: (sprintId, taskId) => api.delete(`/sprints/${sprintId}/tasks/${taskId}`),
  burndown: (sprintId) => api.get(`/sprints/${sprintId}/burndown`),
};


// ==================== GOUVERNANCE V3 ====================
export const governanceApi = {
  risks: (projectId) => api.get('/governance/risks', { params: { project_id: projectId } }),
  createRisk: (data) => api.post('/governance/risks', data),
  updateRisk: (id, data) => api.put(`/governance/risks/${id}`, data),
  deleteRisk: (id) => api.delete(`/governance/risks/${id}`),
  decisions: (projectId) => api.get('/governance/decisions', { params: { project_id: projectId } }),
  createDecision: (data) => api.post('/governance/decisions', data),
  deleteDecision: (id) => api.delete(`/governance/decisions/${id}`),
  audit: (projectId, limit=100) => api.get('/governance/audit', { params: { project_id: projectId, limit } }),
};

// ==================== DOCUMENTS ====================
export const documentsApi = {
  getByProject: (projectId) => api.get(`/documents/${projectId}`),
  upload: (projectId, file, description = '', category = 'other', source_type = 'project', source_id = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);
    formData.append('category', category);
    formData.append('source_type', source_type);
    formData.append('source_id', source_id);
    return api.post(`/documents/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (docId) => api.delete(`/documents/${docId}`),
  toggleClientVisible: (docId, client_visible) => api.put(`/documents/${docId}/client-visible`, { client_visible }),
};

// ==================== NOTIFICATIONS ====================
export const notificationsApi = {
  getAll: (unreadOnly = false, params = {}) => api.get('/notifications', { params: { unread_only: unreadOnly, ...params } }),
  getCount: () => api.get('/notifications/count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
};

// ==================== PORTAIL CLIENT ====================
export const clientPortalApi = {
  getSummary: () => api.get('/client-portal/summary'),
  getComments: (projectId) => api.get(`/client-portal/comments/${projectId}`),
  addComment: (projectId, message) => api.post(`/client-portal/comments/${projectId}`, { message }),
  uploadDocument: (projectId, file, description='', category='other') => { const fd = new FormData(); fd.append('file', file); fd.append('description', description); fd.append('category', category); return api.post(`/client-portal/documents/${projectId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
};


export const auditApi = {
  getLogs: (params = {}) => api.get('/audit/logs', { params }),
  getProjectHistory: (projectId) => api.get(`/audit/project/${projectId}`),
};

// ==================== DASHBOARD ====================
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getProjectStats: (projectId) => api.get(`/dashboard/stats/${projectId}`),
  getPerformance: () => api.get('/dashboard/performance'),
};

// ==================== ADMIN ====================
export const adminApi = {
  getUsers: () => api.get('/admin/users'),
  getVisibleUsers: () => api.get('/admin/users-visible'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, null, { params: { role } }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  notifyUser: (userId) => api.post(`/admin/users/${userId}/notify`),
  testEmail: () => api.post('/admin/email/test'),
  updateUserTheme: (userId, theme) => api.put(`/admin/users/${userId}/theme`, { theme }),
  uploadUserAvatar: (userId, file) => { const formData = new FormData(); formData.append('file', file); return api.post(`/admin/users/${userId}/avatar`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  deleteUserAvatar: (userId) => api.delete(`/admin/users/${userId}/avatar`),
  getReferentials: () => api.get('/admin/referentials'),
  createReferential: (data) => api.post('/admin/referentials', data),
  updateReferential: (id, data) => api.put(`/admin/referentials/${id}`, data),
  deleteReferential: (id) => api.delete(`/admin/referentials/${id}`),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: () => api.delete('/admin/logo'),
  uploadLogoApp: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/logo-app', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogoApp: () => api.delete('/admin/logo-app'),
  uploadLoginBackground: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/login-background', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLoginBackground: () => api.delete('/admin/login-background'),
  getRoles: () => api.get('/admin/roles'),
  getEmailTemplates: () => api.get('/admin/email-templates'),
  createEmailTemplate: (data) => api.post('/admin/email-templates', data),
  updateEmailTemplate: (id, data) => api.put(`/admin/email-templates/${id}`, data),
  deleteEmailTemplate: (id) => api.delete(`/admin/email-templates/${id}`),
  getTeams: () => api.get('/teams'),
  getServices: () => api.get('/services'),
  getLicense: () => api.get('/admin/license'),
  updateLicense: (data) => api.put('/admin/license', data),
  loadDemoData: () => api.post('/admin/demo-data'),
  resetData: () => api.post('/admin/reset-data'),
  testSmtp: (to_email) => api.post('/admin/test-smtp', { to_email }),
  getDemoStatus: () => api.get('/admin/demo-status'),
  generateLicense: (data) => api.post('/admin/generate-license', data),
  getProviderCompanies: () => api.get('/admin/provider-companies'),
  createProviderCompany: (data) => api.post('/admin/provider-companies', data),
  updateProviderCompany: (id, data) => api.put(`/admin/provider-companies/${id}`, data),
  deleteProviderCompany: (id) => api.delete(`/admin/provider-companies/${id}`),
  getEmailTemplateVariables: () => api.get('/admin/email-templates/variables'),
  testEmailTemplate: (data) => api.post('/admin/email-templates/test', data),
  getBiApiTokens: () => api.get('/admin/bi-api-tokens'),
  createBiApiToken: (data) => api.post('/admin/bi-api-tokens', data),
  revokeBiApiToken: (id) => api.delete(`/admin/bi-api-tokens/${id}`),
  getNumbering: () => api.get('/admin/numbering'),
  updateNumbering: (schemes) => api.put('/admin/numbering', { schemes }),
  setNumberingCounter: (entity, next_value, context = {}) => api.post(`/admin/numbering/${entity}/counter`, { next_value, ...context }),
  getSettingsHistory: (limit = 300) => api.get('/admin/settings-history', { params: { limit } }),
  getConnectionHistory: (limit = 300) => api.get('/admin/connection-history', { params: { limit } }),
  getApiConnectionHistory: (limit = 300) => api.get('/admin/api-connection-history', { params: { limit } }),
  getProcessingLogs: (params = {}) => api.get('/admin/processing-logs', { params }),
  processingLogsXlsxUrl: (day) => `${API_URL}/api/admin/processing-logs/report.xlsx?day=${encodeURIComponent(day)}`,
  processingLogsPdfUrl: (day) => `${API_URL}/api/admin/processing-logs/report.pdf?day=${encodeURIComponent(day)}`,
  getVersionHistory: (limit = 100) => api.get('/admin/version-history', { params: { limit } }),
  rollbackVersion: (version) => api.post(`/admin/version-history/rollback/${encodeURIComponent(version)}`),
  getSupervision: () => api.get('/admin/supervision'),
  getEditorUpdateStatus: () => api.get('/admin/editor-updates/status'),
  claimEditorUpdateAccess: () => api.post('/admin/editor-updates/claim'),
  getEditorUpdateHistory: (limit = 100) => api.get('/admin/editor-updates/history', { params: { limit } }),
  uploadEditorUpdate: (file, expectedSha256 = '') => { const fd = new FormData(); fd.append('file', file); fd.append('expected_sha256', expectedSha256 || ''); return api.post('/admin/editor-updates/packages', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  preflightEditorUpdate: (packageId) => api.post(`/admin/editor-updates/preflight/${packageId}`),
  installEditorUpdate: (packageId) => api.post(`/admin/editor-updates/install/${packageId}`),
  getEditorUpdateLog: (packageId, params = {}) => api.get(`/admin/editor-updates/logs/${packageId}`, { params }),
  editorUpdatePackageUrl: (packageId) => `${API_URL}/api/admin/editor-updates/packages/${packageId}/download`,
  editorUpdateLogUrl: (packageId) => `${API_URL}/api/admin/editor-updates/logs/${packageId}/download`,
};

// ==================== GOOGLE AGENDA ====================
// REGLE DE GESTION : cette integration ne remplace pas l'authentification Planigo.
// Elle rattache le compte Google au compte Planigo deja authentifie.
export const googleCalendarApi = {
  status: () => api.get('/integrations/google/status'),
  connect: () => api.post('/integrations/google/connect'),
  disconnect: () => api.delete('/integrations/google/disconnect'),
  sync: (data = {}) => api.post('/integrations/google/sync', data),
  createProjectMeeting: (data) => api.post('/integrations/google/events/project', data),
  createTaskEvent: (taskId) => api.post(`/integrations/google/events/task/${taskId}`),
  getAdminConfig: () => api.get('/integrations/google/admin-config'),
  updateAdminConfig: (data) => api.put('/integrations/google/admin-config', data),
  testAdminConfig: () => api.post('/integrations/google/admin-test'),
  getAdminUsers: () => api.get('/integrations/google/admin-users'),
  setUserEnabled: (userId, enabled) => api.put(`/integrations/google/admin-users/${userId}`, { enabled }),
};

// ==================== API DOCS ====================
export const docsApi = {
  getEndpoints: () => api.get('/docs/endpoints'),
};

// ==================== BUDGETS & MATERIALS ====================
export const budgetsApi = {
  getLines: (projectId) => api.get(`/budgets/lines/${projectId}`),
  createLine: (data) => api.post('/budgets/lines', data),
  updateLine: (id, data) => api.put(`/budgets/lines/${id}`, data),
  deleteLine: (id) => api.delete(`/budgets/lines/${id}`),
  getMaterials: (projectId) => api.get(`/budgets/materials/${projectId}`),
  createMaterial: (data) => api.post('/budgets/materials', data),
  updateMaterial: (id, data) => api.put(`/budgets/materials/${id}`, data),
  deleteMaterial: (id) => api.delete(`/budgets/materials/${id}`),
  getSummary: (projectId) => api.get(`/budgets/summary/${projectId}`),
  getVentilation: (projectId) => api.get(`/budgets/ventilation/${projectId}`),
  getPortfolio: () => api.get(`/budgets/portfolio`),
  importPurchases: (projectId, file) => { const fd=new FormData(); fd.append('file',file); return api.post(`/budgets/import-purchases/${projectId}`, fd, { headers:{'Content-Type':'multipart/form-data'} }); },
  attachDocument: (lineId, documentId) => api.post(`/budgets/lines/${lineId}/documents/${documentId}`),
  uploadLineDocument: (lineId,file,category='budget') => { const fd=new FormData(); fd.append('file',file); fd.append('category',category); return api.post(`/budgets/lines/${lineId}/document-upload`,fd,{headers:{'Content-Type':'multipart/form-data'}}); },
  addLineDocumentLink: (lineId,data) => api.post(`/budgets/lines/${lineId}/document-link`,data),
  exportCSV: (projectId) => `${API_URL}/api/budgets/export/${projectId}`,
};

// ==================== SEARCH & BI ====================
export const searchApi = {
  search: (q) => api.get('/search', { params: { q } }),
};

export const biApi = {
  overview: () => api.get('/bi/overview'),
  timeByProject: (params) => api.get('/bi/time-by-project', { params }),
  workload: () => api.get('/bi/workload'),
  trends: (months) => api.get('/bi/trends', { params: { months } }),
};


// ==================== PLAN DE CHARGE ====================
export const workloadApi = {
  plan: (params) => api.get('/workload/plan', { params }),
  getRecurring: (userId) => api.get('/workload/recurring', { params: userId ? { user_id: userId } : undefined }),
  createRecurring: (data) => api.post('/workload/recurring', data),
  updateRecurring: (id, data) => api.put(`/workload/recurring/${id}`, data),
  deleteRecurring: (id) => api.delete(`/workload/recurring/${id}`),
  exportXlsx: (data) => api.post('/workload/export/xlsx', data, { responseType: 'blob' }),
};

// ==================== ABSENCES ====================
export const absencesApi = {
  getAll: (params) => api.get('/absences', { params }),
  create: (data) => api.post('/absences', data),
  update: (id, data) => api.put(`/absences/${id}`, data),
  delete: (id) => api.delete(`/absences/${id}`),
  exportExcelUrl: (start, end) => `${API_URL}/api/absences/export-excel?start_date=${start}&end_date=${end}`,
  importExcel: (file) => { const fd=new FormData(); fd.append('file',file); return api.post('/absences/import-excel',fd,{headers:{'Content-Type':'multipart/form-data'}}); },
  getCounters: (params) => api.get('/absences/counters',{params}),
  addCounterMovement: (data) => api.post('/absences/counters/movement',data),
};

// ==================== FILES ====================
export const filesApi = {
  getUrl: (filename) => `${API_URL}/api/files/${filename}`,
};

// ==================== ROLES ====================
export const rolesApi = {
  getAll: () => api.get('/roles'),
  getPermissionsMatrix: () => api.get('/roles/permissions-matrix'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// ==================== NOTIFICATION RULES ====================
export const notificationRulesApi = {
  getAll: () => api.get('/notification-rules'),
  getOptions: () => api.get('/notification-rules/options'),
  create: (data) => api.post('/notification-rules', data),
  update: (id, data) => api.put(`/notification-rules/${id}`, data),
  delete: (id) => api.delete(`/notification-rules/${id}`),
  runAll: () => api.post('/cron/check-notifications'),
  runOne: (id) => api.post(`/cron/run-rule/${id}`),
  test: (id) => api.post(`/notification-rules/${id}/test`),
  getDeliveryLog: (limit = 200) => api.get('/notification-rules/delivery-log', { params: { limit } }),
};


// ==================== PROJECT EXPORTS ====================
export const projectExportApi = {
  excelUrl: (projectId) => `${API_URL}/api/projects/${projectId}/export-excel`,
  pdfData: (projectId) => api.get(`/projects/${projectId}/export-pdf-data`),
};

// ==================== CSV TEMPLATE ====================
export const csvTemplateUrl = `${API_URL}/api/tasks/csv-template`;
export const testsCsvTemplateUrl = `${API_URL}/api/tests/csv-template`;

export default api;

// ==================== V5 - PARC / INTERVENTIONS / REUNIONS ====================
// REGLE DE GESTION : les objets IT restent relies aux projets sans dupliquer la logique metier.
export const assetsApi = {
  getAll: (params = {}) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  getByPublicToken: (token) => api.get(`/assets/public/${token}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  loan: (id, data) => api.post(`/assets/${id}/loan`, data),
  returnLoan: (id, data) => api.post(`/assets/${id}/return-loan`, data),
  getLoans: (id) => api.get(`/assets/${id}/loans`),
  getAllLoans: (params = {}) => api.get('/assets/operations/loans', { params }),
  createSav: (id, data) => api.post(`/assets/${id}/sav`, data),
  getSav: (id) => api.get(`/assets/${id}/sav`),
  getAllSav: (params = {}) => api.get('/assets/operations/sav', { params }),
  updateSav: (id, data) => api.put(`/assets/sav/${id}`, data),
  getPublicSav: (token) => api.get(`/assets/public/sav/${token}`),
  regenerateSavPublicToken: (id) => api.post(`/assets/sav/${id}/public-token/regenerate`),
  getAuditTemplates: () => api.get('/assets/audit/templates'),
  createAuditTemplate: (data) => api.post('/assets/audit/templates', data),
  updateAuditTemplate: (id, data) => api.put(`/assets/audit/templates/${id}`, data),
  createAudit: (id, data) => api.post(`/assets/${id}/audits`, data),
  getAudits: (id) => api.get(`/assets/${id}/audits`),
  getAllAudits: (params = {}) => api.get('/assets/operations/audits', { params }),
  getLabelTemplates: () => api.get('/assets/label/templates'),
  createLabelTemplate: (data) => api.post('/assets/label/templates', data),
  networkDiagnostic: (id) => api.post(`/assets/${id}/network-diagnostic`),
  uploadPhoto: (id, file) => { const fd=new FormData(); fd.append('file',file); return api.post(`/assets/${id}/photo`,fd,{headers:{'Content-Type':'multipart/form-data'}}); },
  uploadDocument: (id, file) => { const fd=new FormData(); fd.append('file',file); return api.post(`/assets/${id}/documents`,fd,{headers:{'Content-Type':'multipart/form-data'}}); },
  addDocumentLink: (id, data) => api.post(`/assets/${id}/document-links`, data),
  deleteDocument: (id, documentId) => api.delete(`/assets/${id}/documents/${documentId}`),
  exportExcelUrl: () => '/api/assets/export.xlsx',
  reportPdfUrl: () => '/api/assets/report.pdf',
};

export const suppliersApi = {
  getAll: (params = {}) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  uploadLogo: (id, file) => { const fd=new FormData(); fd.append('file',file); return api.post(`/suppliers/${id}/logo`,fd,{headers:{'Content-Type':'multipart/form-data'}}); },
  uploadDocument: (id, file) => { const fd=new FormData(); fd.append('file',file); return api.post(`/suppliers/${id}/documents`,fd,{headers:{'Content-Type':'multipart/form-data'}}); },
  addDocumentLink: (id, data) => api.post(`/suppliers/${id}/documents/link`, data),
  getCatalog: (id, params = {}) => api.get(`/suppliers/${id}/catalog`, { params }),
  getCatalogs: (id) => api.get(`/suppliers/${id}/catalogs`),
  controlCatalog: (id, catalogId) => api.get(`/suppliers/${id}/catalogs/${catalogId}/control`),
  createCatalog: (id, data) => api.post(`/suppliers/${id}/catalogs`, data),
  updateCatalog: (id, catalogId, data) => api.put(`/suppliers/${id}/catalogs/${catalogId}`, data),
  createCatalogItem: (id, data) => api.post(`/suppliers/${id}/catalog`, data),
  updateCatalogItem: (id, catalogId, data) => api.put(`/suppliers/${id}/catalog/${catalogId}`, data),
  uploadCatalogPhoto: (id, catalogId, file) => { const fd=new FormData(); fd.append('file',file); return api.post(`/suppliers/${id}/catalog/${catalogId}/photo`,fd,{headers:{'Content-Type':'multipart/form-data'}}); },
  catalogPdfUrl: (id) => `/api/suppliers/${id}/catalog.pdf`,
  catalogHeaderPdfUrl: (id, catalogId) => `/api/suppliers/${id}/catalogs/${catalogId}/export.pdf`,
  catalogHeaderXlsxUrl: (id, catalogId) => `/api/suppliers/${id}/catalogs/${catalogId}/export.xlsx`,
  qrUrl: (id) => `/api/suppliers/${id}/qr.svg`,
};

export const serviceCatalogApi = {
  getAll: (params = {}) => api.get('/service-catalog', { params }),
  create: (data) => api.post('/service-catalog', data),
  update: (id, data) => api.put(`/service-catalog/${id}`, data),
  delete: (id) => api.delete(`/service-catalog/${id}`),
};

export const publicInterventionsApi = {
  get: (token) => api.get(`/public/interventions/${token}`),
  satisfaction: (token, data) => api.post(`/public/interventions/${token}/satisfaction`, data),
};

export const interventionsApi = {
  getAll: (params = {}) => api.get('/interventions', { params }),
  getById: (id) => api.get(`/interventions/${id}`),
  create: (data) => api.post('/interventions', data),
  update: (id, data) => api.put(`/interventions/${id}`, data),
  getDashboardSummary: () => api.get(`/interventions/dashboard/summary`),
  addBillingLine: (id, data) => api.post(`/interventions/${id}/billing-lines`, data),
  updateBillingLine: (id, lineId, data) => api.put(`/interventions/${id}/billing-lines/${lineId}`, data),
  deleteBillingLine: (id, lineId) => api.delete(`/interventions/${id}/billing-lines/${lineId}`),
  regeneratePublicToken: (id) => api.post(`/interventions/${id}/public-token/regenerate`),
  addComment: (id, comment) => api.post(`/interventions/${id}/comments`, { comment }),
  uploadPhoto: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/interventions/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  updatePhoto: (id, itemId, data) => api.put(`/interventions/${id}/photos/${itemId}`, data),
  deletePhoto: (id, itemId) => api.delete(`/interventions/${id}/photos/${itemId}`),
  uploadDocument: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/interventions/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  deleteDocument: (id, itemId) => api.delete(`/interventions/${id}/documents/${itemId}`),
  exportExcelUrl: () => '/api/interventions/export.xlsx',
  reportPdfUrl: () => '/api/interventions/report.pdf',
};


// ==================== V6 - MINI GMAO IT ====================
// REGLE DE GESTION : la GMAO reste limitee a l exploitation IT.
export const gmaoApi = {
  summary: () => api.get('/gmao/summary'),
  getStoreItems: (params = {}) => api.get('/gmao/store/items', { params }),
  createStoreItem: (data) => api.post('/gmao/store/items', data),
  updateStoreItem: (id, data) => api.put(`/gmao/store/items/${id}`, data),
  getStockMovements: (params = {}) => api.get('/gmao/store/movements', { params }),
  createStockMovement: (id, data) => api.post(`/gmao/store/items/${id}/movements`, data),
  getStoreItem: (id) => api.get(`/gmao/store/items/${id}`),
  uploadStoreItemPhoto: (id,file) => { const fd=new FormData(); fd.append('file',file); return api.post(`/gmao/store/items/${id}/photo`,fd,{headers:{'Content-Type':'multipart/form-data'}}); },
  uploadStoreItemDocument: (id,file) => { const fd=new FormData(); fd.append('file',file); return api.post(`/gmao/store/items/${id}/documents`,fd,{headers:{'Content-Type':'multipart/form-data'}}); },
  addStoreItemDocumentLink: (id,data) => api.post(`/gmao/store/items/${id}/document-links`,data),
  deleteStoreItemDocument: (id,documentId) => api.delete(`/gmao/store/items/${id}/documents/${documentId}`),
  getReservations: (params={}) => api.get('/gmao/store/reservations',{params}),
  createReservation: (data) => api.post('/gmao/store/reservations',data),
  releaseReservation: (id) => api.post(`/gmao/store/reservations/${id}/release`),
  consumeReservation: (id,data={}) => api.post(`/gmao/store/reservations/${id}/consume`,data),
  getMovementTypes: () => api.get('/gmao/store/movement-types'),
  updateMovementTypes: (types) => api.put('/gmao/store/movement-types',{types}),
  createGenericMovement: (data) => api.post('/gmao/store/movements',data),
  transferStock: (data) => api.post('/gmao/store/transfers',data),
  getInventorySessions: () => api.get('/gmao/inventory/sessions'),
  createInventorySession: (data={}) => api.post('/gmao/inventory/sessions',data),
  getInventorySession: (id) => api.get(`/gmao/inventory/sessions/${id}`),
  countInventoryLine: (id,data) => api.put(`/gmao/inventory/sessions/${id}/count`,data),
  validateInventorySession: (id) => api.post(`/gmao/inventory/sessions/${id}/validate`),
  getOrders: (params = {}) => api.get('/gmao/orders', { params }),
  orderPdfUrl: (id) => `/api/gmao/orders/${id}/pdf`,
  orderXlsxUrl: (id) => `/api/gmao/orders/${id}/xlsx`,
  sendOrder: (id, data={}) => api.post(`/gmao/orders/${id}/send`, data),
  createOrder: (data) => api.post('/gmao/orders', data),
  updateOrder: (id, data) => api.put(`/gmao/orders/${id}`, data),
  // V6.5 : le Bon de Réception est un objet autonome. Sa validation crée l'entrée stock.
  getReceipts: (params = {}) => api.get('/gmao/receipts', { params }),
  createReceipt: (orderId, data) => api.post(`/gmao/orders/${orderId}/receipts`, data),
  updateReceipt: (id, data) => api.put(`/gmao/receipts/${id}`, data),
  validateReceipt: (id) => api.post(`/gmao/receipts/${id}/validate`),
  correctReceipt: (id, data) => api.post(`/gmao/receipts/${id}/corrections`, data),
  receiptPdfUrl: (id) => `/api/gmao/receipts/${id}/pdf`,
  receiveOrder: (id, data) => api.post(`/gmao/orders/${id}/receive`, data), // compatibilité V6.4
  // V6.6 : contrôle trois voies Commande -> BR -> Facture fournisseur.
  getSupplierInvoices: (params = {}) => api.get('/gmao/supplier-invoices', { params }),
  getSupplierInvoice: (id) => api.get(`/gmao/supplier-invoices/${id}`),
  createSupplierInvoice: (data) => api.post('/gmao/supplier-invoices', data),
  updateSupplierInvoice: (id, data) => api.put(`/gmao/supplier-invoices/${id}`, data),
  reconcileSupplierInvoice: (id) => api.post(`/gmao/supplier-invoices/${id}/reconcile`),
  cancelSupplierInvoice: (id) => api.post(`/gmao/supplier-invoices/${id}/cancel`),
  supplierInvoicePdfUrl: (id) => `/api/gmao/supplier-invoices/${id}/pdf`,
  // V6.7 : préparation facturation électronique / interfaces sans envoi automatique.
  supplierInvoiceRegulatoryStatus: (id) => api.get(`/gmao/supplier-invoices/${id}/regulatory-status`),
  supplierInvoiceExportUrl: (id, format) => `/api/gmao/supplier-invoices/${id}/export/${format}`,
  supplierInvoicesInterfaceCsvUrl: () => `/api/gmao/supplier-invoices-exports/interface.csv`,
  supplierInvoicesEreportingCsvUrl: () => `/api/gmao/supplier-invoices-exports/e-reporting.csv`,
  planning: (days = 30) => api.get('/gmao/planning', { params: { days } }),
  reporting: () => api.get('/gmao/reporting'),
  getParkGroups: () => api.get('/gmao/park-groups'),
  createParkGroup: (data) => api.post('/gmao/park-groups', data),
  updateParkGroup: (id,data) => api.put(`/gmao/park-groups/${id}`, data),
  getPurchaseReferentials: (params = {}) => api.get('/gmao/purchase-referentials', { params }),
  createPurchaseReferential: (data) => api.post('/gmao/purchase-referentials', data),
  updatePurchaseReferential: (id, data) => api.put(`/gmao/purchase-referentials/${id}`, data),
  deletePurchaseReferential: (id) => api.delete(`/gmao/purchase-referentials/${id}`),
};

export const meetingsApi = {
  getAll: (projectId) => api.get('/meetings', { params: { project_id: projectId } }),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  createActions: (id, data = {}) => api.post(`/meetings/${id}/actions`, data),
};

// ==================== V5 - MODELES DE DOCUMENTS ====================
// REGLE DE GESTION : les bons d'intervention, retours SAV et CR utilisent des modeles administrables.
export const documentTemplatesApi = {
  getAll: (kind) => api.get('/document-templates', { params: kind ? { kind } : {} }),
  create: (data) => api.post('/document-templates', data),
  update: (id, data) => api.put(`/document-templates/${id}`, data),
};
